import { navigate } from '@cp/src/utils/ApiHelper';
import { useEffect, useState } from 'react';
import styles from './index.module.scss';
import cx from 'classnames';
import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import ExperimentDrawer from './ExperimentDrawer';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { CircularLoaderIcon } from '@navi/web-ui/lib/icons';
import {
  getAllExperiments,
  getSingleExperimentData,
  updateExperiment
} from '../../actions/FieldAllocationAction';
import { ExperimentStatus } from './constants';
import { OrchestrationStatus } from './interfaces';
import DeleteConfirmationModal from './modal/DeleteConfirmationModal';
import { allocationExperimentTableColumnDefs } from './utils';
import EmptyExperimentsList from './EmptyExperimentsList';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import {
  setExperimentData,
  setFailureReport,
  setIsLoading
} from '../../reducers/FieldAllocationSlice';
import UploadLanDrawer from './UploadLanDrawer';

const CaseExclusionExperiment = () => {
  const dispatch = useDispatch();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [uploadLanDrawerOpen, setUploadLanDrawerOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [experimentName, setExperimentName] = useState<string | null>(null);
  const [experimentId, setExperimentId] = useState<string | null>(null);

  const allExperimentsList = useSelector(
    (state: RootState) => state?.fieldAllocation?.experimentsList
  );
  const experimentsListLoading = useSelector(
    (state: RootState) => state?.fieldAllocation?.experimentsListLoading
  );

  const goBack = () => {
    navigate(-1);
  };

  const handleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
    setExperimentId(null);
    dispatch(setExperimentData(null));
    dispatch(setFailureReport(null));
  };

  const onUploadClick = (experimentId: string) => {
    dispatch(getSingleExperimentData(experimentId));
    setUploadLanDrawerOpen(true);
  };

  useEffect(() => {
    dispatch(getAllExperiments());
  }, []);

  useEffect(() => {
    if (!isDeleteConfirmationModalOpen) setExperimentName(null);
  }, [isDeleteConfirmationModalOpen]);

  const handleModalToggle = () => {
    setIsDeleteConfirmationModalOpen(!isDeleteConfirmationModalOpen);
  };

  const handleEditClick = (experimentId: string) => {
    dispatch(getSingleExperimentData(experimentId));
    setExperimentId(experimentId);
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (experimentName: string, experimentId: string) => {
    if (isDeleteConfirmationModalOpen) {
      setExperimentName(null);
      setExperimentId(null);
    } else {
      setExperimentName(experimentName);
      setExperimentId(experimentId);
    }

    handleModalToggle();
  };

  const deleteSuccessCallback = () => {
    setIsDeleteConfirmationModalOpen(false);
    toast('Experiment deleted successfully', { type: 'success' });
    dispatch(getAllExperiments());
  };

  const handleDeleteExperiment = () => {
    const payload = {
      id: experimentId as string,
      file: null,
      status: ExperimentStatus.DELETED
    };
    dispatch(updateExperiment(experimentId as string, payload, deleteSuccessCallback));
  };

  if (experimentsListLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <CircularLoaderIcon size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className={cx(styles.topNavContainer, 'gap-3')}>
        <div className="flex">
          <BackIcon className="cursor-pointer mr-3" size={'16'} onClick={goBack} />
          <Typography variant="h2" color="var(--light-grayscale-content-2)">
            Allocation Experiments
          </Typography>
        </div>
        <Button className={styles.button} onClick={handleDrawer}>
          Create Experiment
        </Button>
      </div>
      {experimentId ? (
        <ExperimentDrawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleDrawer}
          setIsDrawerOpen={setIsDrawerOpen}
          isEditPage
        />
      ) : (
        <ExperimentDrawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleDrawer}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      )}

      <UploadLanDrawer
        isDrawerOpen={uploadLanDrawerOpen}
        onClose={() => setUploadLanDrawerOpen(false)}
        setIsDrawerOpen={setUploadLanDrawerOpen}
      />

      <div className="h-[calc(100vh-170px)] m-9">
        <NaviTable
          tableClasses="!h-[100]"
          data={allExperimentsList || []}
          columns={allocationExperimentTableColumnDefs(
            handleEditClick,
            handleDeleteClick,
            onUploadClick
          )}
          enableRowClick={() => false}
          showPaginationComponent={false}
          emptyDataComponent={<EmptyExperimentsList />}
        />
      </div>
      {isDeleteConfirmationModalOpen ? (
        <DeleteConfirmationModal
          isModalOpen={isDeleteConfirmationModalOpen}
          onClose={handleModalToggle}
          handleDeleteExperiment={handleDeleteExperiment}
          title={`Are you sure you want to delete ${experimentName} from the experiment list?`}
        />
      ) : null}
    </div>
  );
};

export default CaseExclusionExperiment;
